import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd'
import { Table, Space, Avatar, Tag, Input, Button, Tooltip, Typography, Card, Row, Col, Statistic, Popconfirm, message, Badge } from 'antd'
import { SearchOutlined, UserAddOutlined, TeamOutlined, UserOutlined, PhoneOutlined, MailOutlined, DeleteOutlined, ClockCircleOutlined, ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import type { BaseRecord } from '@refinedev/core'
import { useInvalidate } from '@refinedev/core'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

const { Text } = Typography

interface WorkflowState {
  workflowCode: string
  currentStateCode: string
  currentStateName: string
  parentStateCode: string | null
  parentStateName: string | null
  icon: string | null
  color: string | null
  responsibleRole: string | null
  isOverdue: boolean
  dueAt: string | null
  enteredAt: string
}

interface Patient extends BaseRecord {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  phone?: string | null
  status: 'ACTIVE' | 'SUSPENDED' | 'DELETED'
  createdAt: string
  updatedAt: string
  image?: string | null
  dateOfBirth?: string | null
  gender?: 'MALE' | 'FEMALE' | 'OTHER' | null
  workflowState?: WorkflowState | null
  _count?: {
    appointments: number
    labResults: number
    orders: number
  }
}

const statusConfig: Record<Patient['status'], { color: string; text: string }> = {
  ACTIVE: { color: 'success', text: 'Aktivan' },
  SUSPENDED: { color: 'warning', text: 'Suspendovan' },
  DELETED: { color: 'default', text: 'Obrisan' },
}

// Workflow parent state colors/icons
const workflowStageConfig: Record<string, { color: string; label: string }> = {
  QUIZ: { color: 'blue', label: 'Upitnik' },
  LAB_TEST: { color: 'purple', label: 'Laboratorija' },
  CONSULTATION: { color: 'cyan', label: 'Konsultacija' },
  PRESCRIPTION: { color: 'orange', label: 'Terapija' },
  TREATMENT: { color: 'green', label: 'Tretman' },
}

// Role labels for responsible role
const roleLabels: Record<string, string> = {
  NURSE: 'Sestra',
  DOCTOR: 'Lekar',
  ADMIN: 'Admin',
  PATIENT: 'Pacijent',
}

export function PatientsListPage() {
  const invalidate = useInvalidate()
  const navigate = useNavigate()

  const { tableProps, searchFormProps } = useTable<Patient>({
    resource: 'patients',
    syncWithLocation: true,
    onSearch: (values) => {
      const searchValues = values as { search?: string }
      return [
        {
          field: 'search',
          operator: 'contains',
          value: searchValues.search,
        },
      ]
    },
  })

  // Permanent delete function (for testing)
  const handlePermanentDelete = async (id: string, email: string) => {
    try {
      const token = localStorage.getItem('admin_access_token')
      const response = await fetch(`${API_URL}/admin/patients/${id}/permanent`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Failed to delete')
      }

      message.success(`Pacijent ${email} trajno obrisan`)
      invalidate({ resource: 'patients', invalidates: ['list'] })
    } catch (error) {
      message.error(`Greska: ${error instanceof Error ? error.message : 'Neuspelo brisanje'}`)
    }
  }

  return (
    <List
      title="Pacijenti"
      headerButtons={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => invalidate({ resource: 'patients', invalidates: ['list'] })}
          >
            Osvezi
          </Button>
          <Link to="/patients/create">
            <Button type="primary" icon={<UserAddOutlined />}>
              Dodaj pacijenta
            </Button>
          </Link>
        </Space>
      }
    >
      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Ukupno pacijenata"
              value={tableProps.pagination && typeof tableProps.pagination === 'object' ? tableProps.pagination.total : 0}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Aktivnih"
              value={tableProps.dataSource?.filter(p => p.status === 'ACTIVE').length || 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="Suspendovanih"
              value={tableProps.dataSource?.filter(p => p.status === 'SUSPENDED').length || 0}
              valueStyle={{ color: '#faad14' }}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Search */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <form {...searchFormProps}>
          <Space>
            <Input
              placeholder="Pretrazi po imenu ili emailu..."
              prefix={<SearchOutlined />}
              style={{ width: 300 }}
              name="search"
              allowClear
            />
            <Button type="primary" htmlType="submit">
              Pretrazi
            </Button>
          </Space>
        </form>
      </Card>

      {/* Table */}
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: 800 }}
        onRow={(record) => ({
          // Whole row is clickable; routes to patient detail.
          // Ignore clicks that originated inside an actual action
          // element so edit/delete/show buttons in the Akcije column
          // still work on their own and don't double-trigger.
          onClick: (e) => {
            const target = e.target as HTMLElement
            if (target.closest('a, button, .ant-popover, input, select, textarea, [role="button"]')) {
              return
            }
            navigate(`/patients/${record.id}`)
          },
          style: { cursor: 'pointer' },
        })}
      >
        <Table.Column
          dataIndex="firstName"
          title="Pacijent"
          render={(_, record: Patient) => (
            <Space>
              <Avatar
                src={record.image}
                icon={!record.image && <UserOutlined />}
                style={{ backgroundColor: '#1677ff' }}
              />
              <div>
                <Link to={`/patients/${record.id}`}>
                  <Text strong style={{ color: '#1677ff', cursor: 'pointer' }}>
                    {record.firstName || record.lastName
                      ? `${record.firstName || ''} ${record.lastName || ''}`.trim()
                      : record.email}
                  </Text>
                </Link>
                {record.dateOfBirth && (
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {new Date(record.dateOfBirth).toLocaleDateString('sr-Latn-RS')}
                    </Text>
                  </div>
                )}
              </div>
            </Space>
          )}
        />
        <Table.Column
          dataIndex="email"
          title="Kontakt"
          render={(email: string, record: Patient) => (
            <Space direction="vertical" size={0}>
              <Space size={4}>
                <MailOutlined style={{ color: '#8c8c8c' }} />
                <Text>{email}</Text>
              </Space>
              {record.phone && (
                <Space size={4}>
                  <PhoneOutlined style={{ color: '#8c8c8c' }} />
                  <Text>{record.phone}</Text>
                </Space>
              )}
            </Space>
          )}
        />
        <Table.Column
          dataIndex="status"
          title="Status"
          render={(status: Patient['status']) => {
            const config = statusConfig[status]
            return <Tag color={config.color}>{config.text}</Tag>
          }}
          filters={[
            { text: 'Aktivan', value: 'ACTIVE' },
            { text: 'Suspendovan', value: 'SUSPENDED' },
            { text: 'Obrisan', value: 'DELETED' },
          ]}
        />
        <Table.Column
          dataIndex="workflowState"
          title="Faza tretmana"
          render={(workflowState: WorkflowState | null | undefined) => {
            if (!workflowState) {
              return <Tag color="default">Nema workflow</Tag>
            }

            const stageConfig = workflowState.parentStateCode
              ? workflowStageConfig[workflowState.parentStateCode]
              : null
            const stageColor = stageConfig?.color || 'default'
            const roleLabel = workflowState.responsibleRole
              ? roleLabels[workflowState.responsibleRole]
              : null

            return (
              <Space direction="vertical" size={0}>
                <Space size={4}>
                  {workflowState.isOverdue ? (
                    <Badge status="error" />
                  ) : (
                    <Badge status="processing" />
                  )}
                  <Tag color={stageColor}>
                    {workflowState.currentStateName}
                  </Tag>
                </Space>
                {roleLabel && (
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    Čeka: {roleLabel}
                  </Text>
                )}
                {workflowState.isOverdue && (
                  <Text type="danger" style={{ fontSize: 11 }}>
                    <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                    Zakasnelo!
                  </Text>
                )}
              </Space>
            )
          }}
        />
        <Table.Column
          dataIndex="createdAt"
          title="Registrovan"
          render={(date: string) => new Date(date).toLocaleDateString('sr-Latn-RS')}
          sorter
        />
        <Table.Column
          dataIndex="_count"
          title="Aktivnost"
          render={(count: Patient['_count']) => (
            <Space size={4}>
              {count && (
                <>
                  <Tooltip title="Termini">
                    <Tag>{count.appointments || 0} termina</Tag>
                  </Tooltip>
                  <Tooltip title="Lab rezultati">
                    <Tag>{count.labResults || 0} rezultata</Tag>
                  </Tooltip>
                </>
              )}
            </Space>
          )}
        />
        <Table.Column
          title="Akcije"
          fixed="right"
          width={200}
          render={(_, record: Patient) => (
            <Space size="small">
              <ShowButton hideText size="small" recordItemId={record.id} />
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton hideText size="small" recordItemId={record.id} />
              <Popconfirm
                title="Trajno brisanje"
                description={`Da li ste sigurni da zelite TRAJNO obrisati ${record.email}? Ova akcija se ne moze ponistiti!`}
                onConfirm={() => handlePermanentDelete(record.id, record.email)}
                okText="Da, obrisi trajno"
                cancelText="Odustani"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Trajno obrisi (TEST)">
                  <Button
                    size="small"
                    danger
                    type="primary"
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          )}
        />
      </Table>
    </List>
  )
}

export { default as PatientShowPage } from './show'
export { default as PatientEditPage } from './edit'
export { default as PatientCreatePage } from './create'
